import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';

import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import TextInput, { Behaviors } from '@adsk/bim360-matrix-react-text-input';
import Button from '@adsk/bim360-matrix-react-button';
import Field from '@adsk/bim360-matrix-react-field';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import infoCircle from '@adsk/bim360-matrix-react-assets/info-circle.svg';

import './_ExportModal.scss';

/**
 * @class ExportModal
 **/
export class ExportModal extends Component {
  /**
   * @constructor ExportModal
   **/
  constructor(props) {
    super(props);

    this.state = {
      title: props.title,
      message: props.message
    };

    this.onTitleChange = this.onTitleChange.bind(this);
    this.onMessageChange = this.onMessageChange.bind(this);

    this.titleInputProps = {
      autoselect: true,
      className: 'ExportModal__title',
      'data-testid': 'ExportModal__title'
    };
    this.titleValidationProps = { validator: this.props.titleValidator };
    this.messageInputProps = {
      behavior: Behaviors.MULTI_LINE,
      className: 'ExportModal__message',
      'data-testid': 'ExportModal__message'
    };
    this.messageValidationProps = { validator: this.props.messageValidator };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.title !== nextProps.title) {
      this.setState({ title: nextProps.title });
    }
  }

  onTitleChange(value) {
    this.setState({ title: value });
  }

  onMessageChange(value) {
    this.setState({ message: value });
  }

  /**
   * Reset fields to their initial state.
   */
  resetFields() {
    this.setState({
      title: this.props.title,
      message: this.props.message
    });
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const Footer = (
      <div>
        <Button
          styleType={Button.StyleType.LINK_SECONDARY}
          className="ExportModal__cancel-button"
          data-testid="ExportModal__cancel-button"
          onClick={() => {
            this.props.resetOnCancel && this.resetFields();
            this.props.onCancel();
          }}
        >
          {this.props[TSymbolProp].translate('EXPORT_REPORT_CANCEL_BUTTON')}
        </Button>
        <Button
          styleType={Button.StyleType.PRIMARY}
          className="ExportModal__export-button"
          data-testid="ExportModal__export-button"
          disabled={this.props.creating}
          onClick={() =>
            this.props.onCreate({ title: this.state.title, message: this.state.message })
          }
        >
          {this.props.creating
            ? this.props[TSymbolProp].translate('EXPORT_REPORT_EXPORTING_BUTTON')
            : this.props[TSymbolProp].translate('EXPORT_REPORT_EXPORT_BUTTON')}
        </Button>
      </div>
    );

    return (
      <SimpleModal
        className="ExportModal"
        show={this.props.show}
        onExited={() => {
          this.props.resetOnCancel && this.resetFields();
          this.props.onExited && this.props.onExited();
        }}
        title={
          this.props.modalTitle || this.props[TSymbolProp].translate('EXPORT_REPORT_MODAL_TITLE')
        }
        container={this.props.container}
        backdrop={this.props.creating ? 'static' : undefined}
        footer={Footer}
      >
        <div className="ExportModal__modal-content">
          <Field
            className="ExportModal__title-field"
            label={this.props[TSymbolProp].translate('EXPORT_REPORT_TITLE_LABEL')}
          >
            <TextInput
              inputProps={this.titleInputProps}
              validationProps={this.titleValidationProps}
              value={this.state.title}
              placeholder={this.props.titlePlaceholder}
              onChange={this.onTitleChange}
            />
          </Field>
          <Field
            className="ExportModal__message-field"
            label={this.props[TSymbolProp].translate('EXPORT_REPORT_MESSAGE_LABEL')}
          >
            <TextInput
              inputProps={this.messageInputProps}
              validationProps={this.messageValidationProps}
              value={this.state.message}
              placeholder={this.props.messagePlaceholder}
              onChange={this.onMessageChange}
            />
          </Field>
          <div className="ExportModal__information">
            <SvgIcon>{infoCircle}</SvgIcon>
            <span>
              {this.props.infoMessage ||
                this.props[TSymbolProp].translate('EXPORT_REPORT_INFORMATION')}
            </span>
          </div>
        </div>
      </SimpleModal>
    );
  }
}

ExportModal.defaultProps = {
  resetOnCancel: false
};

ExportModal.propTypes = {
  /**
   * ({title, message}) => {}
   * Callback that's called when the user clicked the create button.
   */
  onCreate: PropTypes.func.isRequired,
  /**
   * () => {}
   * Callback that's called when the user clicked the cancel button.
   */
  onCancel: PropTypes.func.isRequired,
  /**
   * () => {}
   * Callback that's called when the modal closes
   */
  onExited: PropTypes.func.isRequired,
  /**
   * A flag indicating whether or not the creation is in progress or not.
   */
  creating: PropTypes.bool.isRequired,
  /**
   * Whether or not to show the modal.
   */
  show: PropTypes.bool.isRequired,
  /**
   * The title of the modal
   * **/
  modalTitle: PropTypes.string,
  /**
   * A container to attach the modal to.
   */
  container: PropTypes.object,
  /**
   * Default title to display.
   */
  title: PropTypes.string,
  /**
   * Default message to display.
   */
  message: PropTypes.string,
  /**
   * Title placeholder to show when the title doesn't have any value.
   */
  titlePlaceholder: PropTypes.string,
  /**
   * Message placeholder to show when the message doesn't have any value.
   */
  messagePlaceholder: PropTypes.string,
  /**
   * A validator object to validate whether or not the title is valid.
   */
  titleValidator: PropTypes.object,
  /**
   * A validator object to validate whether or not the message is valid.
   */
  messageValidator: PropTypes.object,
  /**
   * The locale to use within the component.
   */
  locale: PropTypes.string,
  /**
   * Reset several props to their initial state when canceling the modal.
   */
  resetOnCancel: PropTypes.bool,
  /**
   * Custom info message
   */
  infoMessage: PropTypes.string
};

export default withLocale(
  ExportModal,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
